import { UserInformationResponseDto } from "@/lib/interfaces/user";
import { ErrorEntity } from "../../components/root/context";

type ApiRequest = <T>(
  route: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  options?: any,
  version?: string,
) => Promise<{ data: T | undefined; error: unknown; status: number }>;

export type ResolveProfileGateResult =
  | { ok: true; user: UserInformationResponseDto }
  | { ok: false; redirected: true }
  | { ok: false; redirected: false; entity?: string };

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getErrorEntity = (error: unknown): string | undefined => {
  const err = error as any;
  return err?.entity ?? err?.error;
};

const getErrorMail = (error: unknown): string | undefined => {
  const err = error as any;
  const mail = err?.mail;
  return typeof mail === "string" ? mail : undefined;
};

/**
 * Resolve the next navigation step purely from backend state.
 * Uses `users/userinfo` as source of truth and optionally retries to avoid transient states
 * right after accepting an invitation or creating a user.
 */
export async function resolveProfileGate(args: {
  apiRequest: ApiRequest;
  navigate: (to: string, opts?: { replace?: boolean }) => void;
  retry?: { attempts: number; delayMs: number };
  keepOnboardingForNoSubscription?: boolean;
}): Promise<ResolveProfileGateResult> {
  const { apiRequest, navigate } = args;
  const attempts = args.retry?.attempts ?? 1;
  const delayMs = args.retry?.delayMs ?? 0;

  for (let attempt = 0; attempt < attempts; attempt++) {
    const res = await apiRequest<UserInformationResponseDto>("users/userinfo", "GET");
    if (res.data) {
      return { ok: true, user: res.data };
    }

    const entity = getErrorEntity(res.error);
    const mail = getErrorMail(res.error);

    if (entity === ErrorEntity.EMailNotVerifiedForbidden) {
      navigate(`/new-onboarding?step=email-verification&mail=${mail ?? ""}`, { replace: true });
      return { ok: false, redirected: true };
    }
    if (entity === ErrorEntity.MFANotAuthorizedForbidden) {
      navigate(`/verification/2fa?mail=${mail ?? ""}`, { replace: true });
      return { ok: false, redirected: true };
    }
    if (
      entity === ErrorEntity.NoUserFoundForbidden ||
      entity === ErrorEntity.NoOrganizationForbidden
    ) {
      // Keep the existing onboarding-driven flow for these.
      navigate("/new-onboarding", { replace: true });
      return { ok: false, redirected: true };
    }

    if (entity === ErrorEntity.NoSubscriptionForbidden) {
      if (args.keepOnboardingForNoSubscription) {
        return { ok: false, redirected: false, entity };
      }
      navigate("/new-onboarding", { replace: true });
      return { ok: false, redirected: true };
    }

    // Unknown / transient error: optionally retry.
    if (attempt < attempts - 1 && delayMs > 0) {
      await sleep(delayMs);
      continue;
    }
    return { ok: false, redirected: false, entity };
  }

  return { ok: false, redirected: false };
}

